import React, { useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Checkbox, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Container, FormControlLabel, LinearProgress, Link, MenuItem, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import { textAnimation, TextAnimationArtifact, TextAnimationCapabilities, TextAnimationJob } from '../services/textAnimation';

const examples = [
  {label: 'Walk & wave', text: 'A person walks forward and waves their right hand.'},
  {label: 'Kick', text: 'A person kicks forward with their right leg.'},
  {label: 'Jump', text: 'A person jumps upward and lands on both feet.'},
  {label: 'Dance', text: 'A person dances, moving their arms and stepping from side to side.'},
];
const active = (job: TextAnimationJob) => ['QUEUED', 'RUNNING'].includes(job.state);
const labels: Record<string, string> = {QUEUED: 'Waiting for the GPU', RUNNING: 'Generating', SUCCEEDED: 'Ready', FAILED: 'Failed', CANCELLED: 'Cancelled'};
const stages: Record<string, string> = {STARTING: 'Starting', LOADING_MODEL: 'Preparing', GENERATING: 'Generating motion', EXPORTING: 'Preparing animation', RENDERING: 'Creating preview', PACKAGING: 'Finishing'};
const message = (e: unknown) => e instanceof Error ? e.message : 'The request could not be completed.';

export function TextAnimation({onBack}: {onBack: () => void}) {
  const [caps, setCaps] = useState<TextAnimationCapabilities | null>(null);
  const [jobs, setJobs] = useState<TextAnimationJob[]>([]);
  const [prompt, setPrompt] = useState(examples[0].text);
  const [duration, setDuration] = useState(5);
  const [seed, setSeed] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [selected, setSelected] = useState('');
  const [video, setVideo] = useState('');
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState('');
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const key = useRef('');
  const pending = useRef(false);
  const mounted = useRef(true);
  const selectedJob = jobs.find(job => job.job_id === selected);
  const available = Boolean(caps?.enabled && caps.configured);
  const maxWords = caps?.max_prompt_words ?? 60;
  const maxCharacters = caps?.max_prompt_characters ?? 1000;
  const words = prompt.trim() ? prompt.trim().split(/\s+/).length : 0;
  const validSeed = seed === '' || (/^\d+$/.test(seed) && Number(seed) <= 2147483647);
  const queueFull = jobs.filter(active).length >= (caps?.max_active ?? 2);
  const canSubmit = available && prompt.trim().length >= 5 && prompt.length <= maxCharacters && words <= maxWords && validSeed && accepted && !busy && !queueFull;

  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
  useEffect(() => {key.current = '';}, [prompt, duration, seed, accepted]);
  useEffect(() => {
    const controller = new AbortController();
    void textAnimation.capabilities(controller.signal).then(value => {setCaps(value);}).catch(e => {if (!controller.signal.aborted) setError(message(e));});
    return () => controller.abort();
  }, [reload]);
  useEffect(() => {
    if (!caps?.enabled) return;
    const controller = new AbortController(); let loading = false;
    const refresh = async () => {
      if (loading) return; loading = true;
      try {
        const list = await textAnimation.list(controller.signal);
        window.dispatchEvent(new Event('credits-updated'));
        if (!controller.signal.aborted) {
          setJobs(list);
          setSelected(id => id || list.find(job => job.artifacts_available)?.job_id || list[0]?.job_id || '');
        }
      } catch (e) {if (!controller.signal.aborted) setError(message(e));}
      finally {loading = false;}
    };
    void refresh(); const timer = window.setInterval(refresh, 4000);
    return () => {controller.abort(); window.clearInterval(timer);};
  }, [caps?.enabled, reload]);
  useEffect(() => {
    setVideo('');
    if (selectedJob?.state !== 'SUCCEEDED') return;
    const controller = new AbortController(); let url = '';
    void textAnimation.artifact(selectedJob.job_id, 'video', controller.signal).then(blob => {
      if (!controller.signal.aborted) {url = URL.createObjectURL(blob); setVideo(url);}
    }).catch(e => {if (!controller.signal.aborted) setError(message(e));});
    return () => {controller.abort(); if (url) URL.revokeObjectURL(url);};
  }, [selectedJob?.job_id, selectedJob?.state]);

  const submit = async () => {
    if (!canSubmit || pending.current || !caps) return;
    pending.current = true; setBusy(true); setError('');
    key.current ||= crypto.randomUUID();
    try {
      const job = await textAnimation.submit(prompt, duration, seed === '' ? null : Number(seed), caps.terms_version, key.current);
      if (mounted.current) {
        setJobs(list => [job, ...list.filter(row => row.job_id !== job.job_id)]);
        setSelected(job.job_id); key.current = ''; setReload(value => value + 1); window.dispatchEvent(new Event('credits-updated'));
      }
    } catch (e) {if (mounted.current) setError(message(e));}
    finally {pending.current = false; if (mounted.current) setBusy(false);}
  };
  const cancel = async (job: TextAnimationJob) => {
    setAction(job.job_id); setError('');
    try {
      const updated = await textAnimation.cancel(job.job_id);
      if (mounted.current) {setJobs(list => list.map(row => row.job_id === updated.job_id ? updated : row)); window.dispatchEvent(new Event('credits-updated'));}
    } catch (e) {if (mounted.current) setError(message(e));}
    finally {if (mounted.current) setAction('');}
  };
  const download = async (job: TextAnimationJob, kind: TextAnimationArtifact) => {
    setAction(job.job_id); setError('');
    try {
      const blob = await textAnimation.artifact(job.job_id, kind);
      const url = URL.createObjectURL(blob); const link = document.createElement('a');
      const extension = kind === 'bvh' ? 'bvh' : kind === 'video' ? 'mp4' : 'json';
      link.href = url; link.download = 'nina-text-animation-' + job.job_id + '.' + extension;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {if (mounted.current) setError(message(e));}
    finally {if (mounted.current) setAction('');}
  };

  return <Container maxWidth="lg" sx={{py: {xs: 3, md: 5}}}>
    <Button onClick={onBack} sx={{mb: 2}}>Back to home</Button>
    <Stack direction="row" alignItems="center" spacing={2} sx={{mb: 1}}>
      <Typography variant="h3" sx={{fontWeight: 700, fontSize: {xs: '2rem', md: '2.8rem'}}}>Text to Animation</Typography>
      {caps?.development_preview && <Chip label="Development preview" color="secondary" size="small" />}
    </Stack>
    <Typography color="text.secondary" sx={{mb: 3, maxWidth: 760}}>Describe a movement, preview it from three angles, and download a body animation for Blender, MakeHuman, Mixamo or Unreal.</Typography>
    {error && <Alert severity="error" onClose={() => setError('')} sx={{mb: 2}}>{error}</Alert>}
    <Dialog open={error.includes('Insufficient credits')} onClose={() => setError('')} fullWidth maxWidth="sm"><DialogTitle>Insufficient credits</DialogTitle><DialogContent><Typography>This animation requires {duration} credits. Buy credits or subscribe to continue.</Typography></DialogContent><DialogActions><Button onClick={() => setError('')}>Close</Button><Button variant="contained" href="/plans">View plans</Button></DialogActions></Dialog>
    {caps && !available && <Alert severity="info" sx={{mb: 2}}>Text to Animation is not available in this environment.</Alert>}
    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'minmax(310px, 0.85fr) minmax(0, 1.3fr)'}, gap: 3}}>
      <Card><CardContent>
        <Typography variant="h6" sx={{mb: 2}}>What should the character do?</Typography>
        <TextField fullWidth multiline minRows={4} label="Movement description" value={prompt}
          onChange={e => setPrompt(e.target.value)} disabled={busy}
          inputProps={{maxLength: maxCharacters}}
          error={words > maxWords}
          helperText={'English · one character · ' + words + '/' + maxWords + ' words'} />
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{my: 2}}>
          {examples.map(example => <Chip key={example.label} label={example.label} onClick={() => setPrompt(example.text)} disabled={busy} variant="outlined" />)}
        </Stack>
        <Stack direction="row" spacing={2} sx={{mb: 2}}>
          <TextField select label="Duration" value={duration} onChange={e => setDuration(Number(e.target.value))} disabled={busy} sx={{minWidth: 125}}>
            {(caps?.durations ?? [2,3,4,5,6,7,8,9]).map(value => <MenuItem key={value} value={value}>{value} seconds</MenuItem>)}
          </TextField>
          <TextField label="Seed (optional)" value={seed} onChange={e => setSeed(e.target.value)} disabled={busy} error={!validSeed}
            helperText="Reuse it to repeat a result" inputProps={{inputMode: 'numeric', maxLength: 10}} fullWidth />
        </Stack>
        <Typography variant="body2" color="text.secondary">Start with one simple action. This preview creates body motion; it does not animate fingers, solve collisions or interact with objects.</Typography>
        <FormControlLabel sx={{my: 1}} control={<Checkbox checked={accepted} disabled={busy} onChange={e => setAccepted(e.target.checked)} />}
          label={<Typography variant="body2">I authorize the reservation of {duration} credits and accept the <Link href="/terms_conditions" target="_blank" rel="noopener">Terms and Conditions</Link>.</Typography>} />
        <Button fullWidth size="large" variant="contained" startIcon={<AutoAwesomeIcon />} disabled={!canSubmit} onClick={() => void submit()}>
          {busy ? 'Submitting…' : 'Generate animation'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>{duration} credits - 1 credit per second of animation. Charged only when completed successfully.</Typography>
        {queueFull && <Alert severity="info" sx={{mt: 2}}>Wait for an active animation to finish before starting another.</Alert>}
      </CardContent></Card>
      <Card><CardContent>
        <Typography variant="h6" sx={{mb: 2}}>Animation preview</Typography>
        {video ? <Box component="video" src={video} controls loop playsInline sx={{width: '100%', borderRadius: 2}} /> :
          <Box sx={{aspectRatio: '16 / 9', bgcolor: 'rgba(128, 100, 160, .09)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', borderRadius: 2, p: 3}}>
            <AutoAwesomeIcon sx={{fontSize: 44, mb: 2, color: 'secondary.main'}} />
            <Typography textAlign="center" color="text.secondary">{selectedJob ? labels[selectedJob.state] || selectedJob.state : 'Your generated movement will appear here.'}</Typography>
          </Box>}
        {selectedJob && <>
          <Stack direction="row" gap={1} alignItems="center" sx={{my: 2}}><Chip label={labels[selectedJob.state] || selectedJob.state} size="small" />
            <Typography variant="body2">{selectedJob.settings.duration}s · seed {selectedJob.settings.seed}</Typography></Stack>
          <Typography variant="body2" sx={{mb: 2}}>{selectedJob.settings.prompt}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
            {selectedJob.billing?.charged ?? 0} credits charged{selectedJob.billing?.reserved ? ` - ${selectedJob.billing.reserved} reserved` : ''}
          </Typography>
          {active(selectedJob) && <><LinearProgress variant="determinate" value={selectedJob.progress} /><Typography variant="caption">{stages[selectedJob.stage] || labels[selectedJob.state]} · {selectedJob.progress}%</Typography>
            <Button size="small" disabled={Boolean(action)} onClick={() => void cancel(selectedJob)} sx={{ml: 2}}>Cancel</Button></>}
          {selectedJob.error && <Alert severity="error">{selectedJob.error}</Alert>}
          {selectedJob.artifacts_available && <Stack direction="row" flexWrap="wrap" gap={1}>
            <Button variant="contained" startIcon={<DownloadIcon />} disabled={Boolean(action)} onClick={() => void download(selectedJob, 'bvh')}>Download BVH</Button>
            <Button variant="outlined" disabled={Boolean(action)} onClick={() => void download(selectedJob, 'video')}>Download preview</Button>
            <Button disabled={Boolean(action)} onClick={() => void download(selectedJob, 'motion')}>Motion data</Button>
          </Stack>}
        </>}
        <Box sx={{mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider'}}>
          <Typography variant="body2">Use the <Link href="/downloads/nina_bvh_makehuman-1.1.1.zip" download>Blender retarget addon</Link> to apply the BVH to MakeHuman, Mixamo or Unreal Manny. Import the motion in meters.</Typography>
        </Box>
      </CardContent></Card>
    </Box>
    <Accordion sx={{mt: 3}}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Animation history ({jobs.length})</Typography></AccordionSummary>
      <AccordionDetails>
        {jobs.length === 0 && <Typography color="text.secondary">No animations yet.</Typography>}
        <Stack spacing={1}>{jobs.map(job => <Box key={job.job_id} sx={{display: 'flex', alignItems: 'center', gap: 2, p: 1, borderBottom: '1px solid', borderColor: 'divider'}}>
          <Box sx={{flex: 1, minWidth: 0}}><Typography variant="body2" noWrap title={job.settings.prompt}>{job.settings.prompt}</Typography><Typography variant="caption" color="text.secondary">{new Date(job.created_at).toLocaleString()} · {labels[job.state] || job.state}</Typography></Box>
          <Button size="small" onClick={() => setSelected(job.job_id)}>View</Button>
          {active(job) && <Button size="small" disabled={Boolean(action)} onClick={() => void cancel(job)}>Cancel</Button>}
        </Box>)}</Stack>
      </AccordionDetails>
    </Accordion>
  </Container>;
}
